import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import apiClient from '../../lib/api-client';

export type DismissalDuration = '3_months' | '6_months' | '1_year' | 'never';

export interface ObservabilityListQuery {
  page?: number;
  pageSize?: number;
  from?: string;
  to?: string;
  search?: string;
  userId?: string;
  salesTeamId?: string;
}

export interface ObservabilityPaginatedResponse<T> {
  rows: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ObservabilitySummary {
  totals: {
    opportunityDismissals: number;
    productDismissals: number;
    forwards: number;
    activeOpportunities: number;
    forwardRate: number;
    opportunityDismissalRate: number;
    productDismissalRate: number;
  };
  dismissalDurations: Array<{ duration: DismissalDuration; count: number }>;
  topDismissedProducts: Array<{
    carrierName: string;
    productName: string;
    productType: string | null;
    count: number;
  }>;
  topDismissedCarriers: Array<{ carrierName: string; count: number }>;
  forwardsOverTime: Array<{ date: string; count: number }>;
}

export interface OpportunityDismissalRow {
  id: string;
  opportunityId: string;
  policyId: string;
  policyNumber: string | null;
  dismissedAt: string;
  dismissedUntil: string | null;
  duration: DismissalDuration;
  comment: string;
  dismissedByEmail: string | null;
  dismissedByName: string | null;
  clientName: string | null;
  carrierName: string | null;
  productName: string | null;
  opportunityScore: number | null;
  salesTeamId: string | null;
  salesTeamName: string | null;
}

export interface ProductDismissalRow {
  id: string;
  opportunityId: string;
  policyId: string;
  policyNumber: string | null;
  dismissedAt: string;
  comment: string;
  dismissedByEmail: string | null;
  carrierName: string;
  productName: string;
  productType: string | null;
  recommendationScore: number | null;
  replacementCarrierName: string | null;
  replacementProductName: string | null;
  salesTeamId: string | null;
  salesTeamName: string | null;
}

export interface ForwardedProductRow {
  productId: string | null;
  carrierName: string;
  productName: string;
  position: number;
  originalPosition: number | null;
  recommendationScore: number | null;
  firelightReady: boolean;
}

export interface ForwardActivityRow {
  id: string;
  opportunityId: string;
  policyId: string | null;
  policyNumber: string | null;
  forwardedAt: string;
  forwardedByEmail: string | null;
  advisorName: string | null;
  clientName: string | null;
  currentCarrier: string | null;
  currentProduct: string | null;
  currentAccountValue: number | null;
  productCount: number;
  forwardedProducts: ForwardedProductRow[];
  summaryEdited: boolean;
  productsReordered: boolean;
  salesTeamId: string | null;
  salesTeamName: string | null;
}

export interface WithdrawalActivityRow extends ForwardActivityRow {
  withdrawnAt: string;
  withdrawnByEmail: string | null;
}

export const observabilityKeys = {
  all: ['observability'] as const,
  summary: () => [...observabilityKeys.all, 'summary'] as const,
  opportunityDismissals: (filters: ObservabilityListQuery) =>
    [...observabilityKeys.all, 'opportunity-dismissals', filters] as const,
  productDismissals: (filters: ObservabilityListQuery) =>
    [...observabilityKeys.all, 'product-dismissals', filters] as const,
  forwards: (filters: ObservabilityListQuery) =>
    [...observabilityKeys.all, 'forwards', filters] as const,
  myOpportunityDismissals: (filters: ObservabilityListQuery) =>
    [...observabilityKeys.all, 'mine', 'opportunity-dismissals', filters] as const,
  myForwards: (filters: ObservabilityListQuery) =>
    [...observabilityKeys.all, 'mine', 'forwards', filters] as const,
  myWithdrawals: (filters: ObservabilityListQuery) =>
    [...observabilityKeys.all, 'mine', 'withdrawals', filters] as const,
};

export function useObservabilitySummary(
  options?: Omit<UseQueryOptions<ObservabilitySummary>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: observabilityKeys.summary(),
    queryFn: async () => {
      const response = await apiClient.get<ObservabilitySummary>('/observability/summary');
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useOpportunityDismissals(
  filters: ObservabilityListQuery,
  options?: Omit<
    UseQueryOptions<ObservabilityPaginatedResponse<OpportunityDismissalRow>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: observabilityKeys.opportunityDismissals(filters),
    queryFn: async () => {
      const response = await apiClient.get<ObservabilityPaginatedResponse<OpportunityDismissalRow>>(
        `/observability/opportunity-dismissals${toQueryString(filters)}`
      );
      return response.data;
    },
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useProductDismissals(
  filters: ObservabilityListQuery,
  options?: Omit<
    UseQueryOptions<ObservabilityPaginatedResponse<ProductDismissalRow>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: observabilityKeys.productDismissals(filters),
    queryFn: async () => {
      const response = await apiClient.get<ObservabilityPaginatedResponse<ProductDismissalRow>>(
        `/observability/product-dismissals${toQueryString(filters)}`
      );
      return response.data;
    },
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useForwardActivity(
  filters: ObservabilityListQuery,
  options?: Omit<
    UseQueryOptions<ObservabilityPaginatedResponse<ForwardActivityRow>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: observabilityKeys.forwards(filters),
    queryFn: async () => {
      const response = await apiClient.get<ObservabilityPaginatedResponse<ForwardActivityRow>>(
        `/observability/forwards${toQueryString(filters)}`
      );
      return response.data;
    },
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useMyOpportunityDismissals(
  filters: ObservabilityListQuery,
  options?: Omit<
    UseQueryOptions<ObservabilityPaginatedResponse<OpportunityDismissalRow>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: observabilityKeys.myOpportunityDismissals(filters),
    queryFn: async () => {
      const response = await apiClient.get<ObservabilityPaginatedResponse<OpportunityDismissalRow>>(
        `/observability/mine/opportunity-dismissals${toQueryString(filters)}`
      );
      return response.data;
    },
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useMyForwardActivity(
  filters: ObservabilityListQuery,
  options?: Omit<
    UseQueryOptions<ObservabilityPaginatedResponse<ForwardActivityRow>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: observabilityKeys.myForwards(filters),
    queryFn: async () => {
      const response = await apiClient.get<ObservabilityPaginatedResponse<ForwardActivityRow>>(
        `/observability/mine/forwards${toQueryString(filters)}`
      );
      return response.data;
    },
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useMyWithdrawalActivity(
  filters: ObservabilityListQuery,
  options?: Omit<
    UseQueryOptions<ObservabilityPaginatedResponse<WithdrawalActivityRow>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: observabilityKeys.myWithdrawals(filters),
    queryFn: async () => {
      const response = await apiClient.get<ObservabilityPaginatedResponse<WithdrawalActivityRow>>(
        `/observability/mine/withdrawals${toQueryString(filters)}`
      );
      return response.data;
    },
    staleTime: 30 * 1000,
    ...options,
  });
}

function toQueryString(filters: ObservabilityListQuery): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }

    params.set(key, String(value));
  }

  const query = params.toString();
  return query ? `?${query}` : '';
}
